import { createMachine, assign } from "xstate-4";
import type { InterpreterFrom } from "xstate-4";
import { xlog } from "vue-app/utils/xstate";
import axios from "vue-app/utils/axios";
import { useActor } from "@xstate-4/vue";
import type { Activity } from "vue-app/pages/machines/machineCustomerPreview";

export type Email = {
  sender: string;
  subject: string;
  html: string;
};

type Context = {
  type: "email" | "product" | null;
  details: Activity | null;
  email: Email | null;
  pages: number;
  currentPage: number;
};

export const machineActivityPreview = createMachine(
  {
    id: "ActivityPreview",
    tsTypes: {} as import("./machineCustomerPreviewActivityPreview.typegen").Typegen0,
    schema: {
      events: {} as { type: "CLOSE" } | { type: "CHANGE_PAGE"; page: number } | { type: "FETCH_EMAIL" },
      context: {} as Context,
      services: {} as {
        fetchEngagementMessage: {
          data: Email;
        };
      },
    },
    context: {
      type: null,
      details: null,
      email: null,
      pages: 1,
      currentPage: 1,
    },
    initial: "displayDetails",
    entry: ["assignPages"],
    states: {
      displayDetails: {
        always: [
          { target: "closed", cond: "areDetailsNullOrTypeDoesNotMatch" },
          { target: "fetchingEmail", cond: "shouldFetchEmail" },
        ],
        tags: ["displayDetails"],
        on: {
          CLOSE: [
            {
              target: "closed",
            },
          ],
          CHANGE_PAGE: [
            {
              actions: ["assignCurrentPage"],
            },
          ],
        },
      },
      fetchingEmail: {
        tags: ["fetchingEmail"],
        invoke: {
          src: "fetchEngagementMessage",
          id: "fetchEngagementMessage",
          onDone: [
            {
              target: "displayDetails",
              actions: ["assignEmail"],
            },
          ],
          onError: [
            {
              target: "errorView",
            },
          ],
        },
      },
      errorView: {
        tags: ["errorView"],
        on: {
          FETCH_EMAIL: [
            {
              target: "fetchingEmail",
            },
          ],
          CLOSE: [
            {
              target: "closed",
            },
          ],
        },
      },
      closed: {
        tags: ["closed"],
        type: "final",
      },
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      assignPages: assign((context, event) => {
        xlog({ label: "assignPages", context, event });
        return { pages: context.details?.products?.length || 1 };
      }),
      assignEmail: assign((context, event) => {
        xlog({ label: "assignEmail", context, event });
        return {
          email: event.data,
        };
      }),
      assignCurrentPage: assign((context, event) => {
        xlog({ label: "assignCurrentPage", context, event });
        return {
          currentPage: event.page,
        };
      }),
    },
    guards: {
      areDetailsNullOrTypeDoesNotMatch: (context, event) => {
        xlog({ label: "areDetailsNullOrTypeDoesNotMatch", context, event });
        return context.details === null || context.type === null;
      },
      shouldFetchEmail: (context, event) => {
        xlog({ label: "shouldFetchEmail", context, event });
        return context.type === "email" && context.email === null;
      },
    },
    services: {
      fetchEngagementMessage: async (context, event) => {
        xlog({ label: "fetchEngagementMessage", context, event });
        const response = await axios.get(
          `/customer-preview/get-email-preview?engagement_message_id=${context.details?.emailEventData?.engagementMessageId}`
        );
        return {
          sender: response.data.data.fromName + " (" + response.data.data.fromEmail + ")",
          subject: response.data.data.subject,
          html: response.data.data.html,
        };
      },
    },
  }
);

export type ActivityPreviewActor = InterpreterFrom<typeof machineActivityPreview>;
// @ts-ignore
export function useActivityPreview(service) {
  return useActor(service.state.value.children.activityPreviewModal as ActivityPreviewActor);
}
